import React from 'react';
import classnames from 'classnames/bind';
import { AddIcon, ArrowLeftIcon, ArrowRightIcon, RemoveIcon, Spinner, ZoomInIcon } from 'tuui';
import { DragAndDropFileContext, LinkButton } from 'taboola-ultimate-ui';
import { ONLY_IMAGE_FILES_NO_GIF } from 'modules/campaigns/modules/creative-creator/config/fileTypeConfigs';
import { MediaDragAndDrop } from 'modules/campaigns/modules/creative-editor/components/MediaDragAndDrop/MediaDragAndDrop';
import { OUTCROPPING_COMPONENT_NAME } from 'modules/campaigns/modules/creative-editor/components/MediaTabs/CroppingToolsForm/constants/croppingModalGtmConstants';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import {
    sendCroppingModalTrackingEvent,
    UNSUPPORTED_FILE_TYPE_VALIDATION_ERROR,
    useCropImageControlsState,
} from '../../hooks/useCropImageControlsState';
import styles from './cropImageControls.module.scss';

const classNameBuilder = classnames.bind(styles);

const ZOOM_STEP = 0.1;

export const CropImageControls = ({
    src,
    ratio,
    updateSrc,
    originalSrc,
    srcHistory,
    setSrcHistory,
    isOutcroppingEnabled,
    shouldShowOutcroppingControls,
    maxZoom,
    zoom,
    setZoom,
    setKey,
    isEqualToInitialData,
}) => {
    const curSrcIndex = srcHistory?.indexOf(src);

    const { onResetClick, showErrorIndication, onReplaceImage, isUploadingReplacedImage } = useCropImageControlsState({
        src,
        originalSrc,
        updateSrc,
        setSrcHistory,
        ratio,
        setKey,
        isOutcroppingEnabled,
        srcHistory,
    });

    return (
        <div className={styles['crop-controls']}>
            <div className={styles['control-container']}>
                <>
                    {isUploadingReplacedImage && <Spinner size={20} className={styles['spinner']} color="info" />}
                    <MediaDragAndDrop
                        suppressDropContainer
                        fileTypes={ONLY_IMAGE_FILES_NO_GIF}
                        multiple={false}
                        onDrop={onReplaceImage}
                        onFileTypeFailure={() => showErrorIndication(UNSUPPORTED_FILE_TYPE_VALIDATION_ERROR)}
                        className={styles['replace-image-drop-zone']}
                        isVertical={ratio < 1}
                    >
                        <DragAndDropFileContext.Consumer>
                            {({ openFileDialog }) => (
                                <LinkButton onClick={openFileDialog} className={styles['control-button']}>
                                    <FormattedMessage id="cropping.content.replace.button" defaultMessage="Replace" />
                                </LinkButton>
                            )}
                        </DragAndDropFileContext.Consumer>
                    </MediaDragAndDrop>
                </>
                {!isEqualToInitialData && (
                    <>
                        <div className={styles['divider']} />
                        <LinkButton onClick={onResetClick} className={styles['control-button']}>
                            <FormattedMessage id="cropping.content.reset.button" defaultMessage="Reset" />
                        </LinkButton>
                    </>
                )}

                {shouldShowOutcroppingControls && (
                    <>
                        <div className={styles['divider']} />
                        <div className={styles['history-container']}>
                            <FormattedMessage id="cropping.content.history.label" defaultMessage="Variations" />
                            <div
                                className={classNameBuilder('arrow', { disabled: curSrcIndex === 0 })}
                                onClick={() => {
                                    updateSrc(srcHistory[curSrcIndex - 1]);
                                    sendCroppingModalTrackingEvent({
                                        component: `${OUTCROPPING_COMPONENT_NAME}: Variations History Clicked`,
                                    });
                                }}
                            >
                                <ArrowLeftIcon />
                            </div>
                            <div>
                                {curSrcIndex + 1}/{srcHistory.length}
                            </div>
                            <div
                                className={classNameBuilder('arrow', {
                                    disabled: curSrcIndex === srcHistory.length - 1,
                                })}
                                onClick={() => {
                                    updateSrc(srcHistory[curSrcIndex + 1]);
                                    sendCroppingModalTrackingEvent({
                                        component: `${OUTCROPPING_COMPONENT_NAME}: Variations History Clicked`,
                                    });
                                }}
                            >
                                <ArrowRightIcon />
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className={styles['zoom-container']}>
                <ZoomInIcon className={styles['zoom-icon']} />
                <div className={styles['zoom-controls']}>
                    <button
                        className={styles['zoom-button']}
                        onClick={() => setZoom(Math.max(zoom - ZOOM_STEP, 1))}
                        disabled={zoom === 1}
                        aria-label="zoom out"
                    >
                        <RemoveIcon fontSize="small" />
                    </button>
                    <div>{Math.round(zoom * 100)}%</div>
                    <button
                        className={styles['zoom-button']}
                        onClick={() => setZoom(Math.min(zoom + ZOOM_STEP, maxZoom))}
                        disabled={zoom === maxZoom}
                        aria-label="zoom in"
                    >
                        <AddIcon fontSize="small" />
                    </button>
                </div>
            </div>
        </div>
    );
};
